/**
 * SVGGrowthLine — Dimus Video Components
 *
 * Linha de crescimento SVG com draw-on via strokeDashoffset.
 * Área de gradient fill sob a linha. Dot animado no endpoint.
 * Suporta múltiplas séries para comparações (boca-a-boca vs tráfego pago).
 *
 * Design ref: v9-verdades-bocaaboca.html (padrão aprovado 30/05/2026)
 * ADR: ia-skills ADR-0001
 *
 * Features:
 * - strokeDashoffset draw-on com spring
 * - Area gradient fill sincronizada
 * - Endpoint dot com glow pulse
 * - Múltiplas séries com stagger
 * - Tooltip label no endpoint (opcional)
 */
import React, { useRef } from "react";
import { interpolate, spring, useCurrentFrame } from "remotion";
import { Easing } from "remotion";

// ── Easings ──────────────────────────────────────────────────────────────────
const ease = {
  expoOut: Easing.bezier(0.19, 1, 0.22, 1),
  power3Out: Easing.bezier(0.215, 0.61, 0.355, 1),
};

// ── Types ─────────────────────────────────────────────────────────────────────
export type GrowthCurveType = "linear" | "slow-start" | "fast-start" | "s-curve";

export interface GrowthSeries {
  /** Cor da linha e do dot. Ex: "#00DF82" */
  color: string;
  /** Cor do gradient fill sob a linha. Default: color com 35% alpha no topo, 4% na base */
  fillColorTop?: string;
  fillColorBottom?: string;
  /** Tipo de curva. Default: "slow-start" */
  curveType?: GrowthCurveType;
  /** Label exibido no endpoint (ex: "Cresce sozinho"). Null = sem label */
  endLabel?: string;
  /** Frame de início desta série. Default: startFrame da prop principal */
  seriesStartFrame?: number;
}

export interface SVGGrowthLineProps {
  /** Séries de dados (mínimo 1) */
  series: GrowthSeries[];
  /** Width do container SVG em px. Default: 240 */
  width?: number;
  /** Height do container SVG em px. Default: 154 */
  height?: number;
  /** Frame de início padrão */
  startFrame?: number;
  /** Duração da animação de draw-on em frames. Default: 40 */
  drawDuration?: number;
  /** Stagger entre séries em frames. Default: 0 */
  stagger?: number;
  /** Espessura da linha em px. Default: 4.5 */
  strokeWidth?: number;
  /** Raio do dot endpoint em px. Default: 7 */
  dotRadius?: number;
  /** Cor e tamanho do label externo */
  labelColor?: string;
  labelFontSize?: number;
}

// ── Curve path generators ────────────────────────────────────────────────────
function buildPath(type: GrowthCurveType, w: number, h: number): string {
  switch (type) {
    case "linear":
      return `M0 ${h} L${w} 0`;

    case "slow-start":
      // Começa devagar, acelera no final — padrão boca-a-boca
      return `M0 ${h * 0.95} C${w * 0.2} ${h * 0.9} ${w * 0.5} ${h * 0.6} ${w * 0.7} ${h * 0.25} C${w * 0.85} ${h * 0.05} ${w} 0 ${w} 0`;

    case "fast-start":
      // Acelera rápido e suaviza — crescimento viral
      return `M0 ${h} C${w * 0.2} ${h * 0.2} ${w * 0.5} ${h * 0.05} ${w} 0`;

    case "s-curve":
      // S-curve sigmoide — adoção de produto
      return `M0 ${h} C${w * 0.15} ${h * 0.98} ${w * 0.35} ${h * 0.85} ${w * 0.5} ${h * 0.5} C${w * 0.65} ${h * 0.15} ${w * 0.85} ${h * 0.02} ${w} 0`;

    default:
      return `M0 ${h * 0.95} C${w * 0.2} ${h * 0.9} ${w * 0.5} ${h * 0.6} ${w * 0.7} ${h * 0.25} C${w * 0.85} ${h * 0.05} ${w} 0 ${w} 0`;
  }
}

// Build area path from line path (close down to bottom)
function buildAreaPath(linePath: string, w: number, h: number): string {
  return `${linePath} L${w} ${h} L0 ${h} Z`;
}

// Approx total path length (used for strokeDasharray)
function approxPathLength(type: GrowthCurveType, w: number, h: number): number {
  // Conservative overestimate so we never see stroke gap at the end
  const hyp = Math.sqrt(w * w + h * h);
  switch (type) {
    case "linear": return hyp;
    case "slow-start": return hyp * 1.25;
    case "fast-start": return hyp * 1.2;
    case "s-curve": return hyp * 1.35;
    default: return hyp * 1.25;
  }
}

// Get endpoint coordinates for the dot
function getEndpoint(w: number, _h: number): { x: number; y: number } {
  return { x: w, y: 0 }; // always top-right
}

// ── Single Series ─────────────────────────────────────────────────────────────
const Series: React.FC<{
  series: GrowthSeries;
  width: number;
  height: number;
  seriesStartFrame: number;
  drawDuration: number;
  strokeWidth: number;
  dotRadius: number;
  labelColor: string;
  labelFontSize: number;
  gradId: string;
}> = ({
  series,
  width,
  height,
  seriesStartFrame,
  drawDuration,
  strokeWidth,
  dotRadius,
  labelColor,
  labelFontSize,
  gradId,
}) => {
  const frame = useCurrentFrame();
  const local = Math.max(0, frame - seriesStartFrame);

  const curveType = series.curveType ?? "slow-start";
  const linePath = buildPath(curveType, width, height);
  const areaPath = buildAreaPath(linePath, width, height);
  const pathLen = approxPathLength(curveType, width, height);

  // Draw-on via strokeDashoffset
  const drawProgress = interpolate(local, [0, drawDuration], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: ease.expoOut,
  });
  const dashOffset = pathLen * (1 - drawProgress);

  // Area opacity follows draw progress
  const areaOp = interpolate(drawProgress, [0, 1], [0, 0.35], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Endpoint dot
  const dotDelay = Math.floor(drawDuration * 0.85);
  const dotSpring = spring({
    frame: Math.max(0, local - dotDelay),
    fps: 30,
    config: { damping: 10, stiffness: 150 },
    from: 0,
    to: 1,
  });
  const dotPulse = 1 + Math.sin(frame * 0.15) * 0.12;
  const ep = getEndpoint(width, height);

  // Fill colors
  const fillTop = series.fillColorTop ?? `${series.color}59`; // 35% alpha
  const fillBottom = series.fillColorBottom ?? `${series.color}0A`; // 4% alpha

  return (
    <g>
      {/* Gradient def */}
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={fillTop} />
          <stop offset="100%" stopColor={fillBottom} />
        </linearGradient>
      </defs>

      {/* Area fill */}
      <path
        d={areaPath}
        fill={`url(#${gradId})`}
        opacity={areaOp}
      />

      {/* Line */}
      <path
        d={linePath}
        fill="none"
        stroke={series.color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={pathLen}
        strokeDashoffset={dashOffset}
      />

      {/* Endpoint dot with glow pulse */}
      <circle
        cx={ep.x}
        cy={ep.y}
        r={dotRadius * dotSpring * dotPulse}
        fill={series.color}
        opacity={dotSpring}
        style={{ filter: `drop-shadow(0 0 ${dotRadius * 1.5}px ${series.color})` }}
      />
    </g>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
export const SVGGrowthLine: React.FC<SVGGrowthLineProps> = ({
  series,
  width = 240,
  height = 154,
  startFrame = 0,
  drawDuration = 40,
  stagger = 0,
  strokeWidth = 4.5,
  dotRadius = 7,
  labelColor = "#555",
  labelFontSize = 20,
}) => {
  // C-004 fix: uid único por instância — evita colisão de gradient IDs entre dois
  // SVGGrowthLine na mesma composição (ambos emitiriam "growth-grad-0" no mesmo DOM)
  const uid = useRef(Math.random().toString(36).slice(2)).current;

  return (
    <div style={{ display: "flex", flexDirection: "column", width }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: "100%", height }}
        preserveAspectRatio="none"
      >
        {series.map((s, i) => (
          <Series
            key={i}
            series={s}
            width={width}
            height={height}
            seriesStartFrame={s.seriesStartFrame ?? startFrame + i * stagger}
            drawDuration={drawDuration}
            strokeWidth={strokeWidth}
            dotRadius={dotRadius}
            labelColor={labelColor}
            labelFontSize={labelFontSize}
            gradId={`growth-grad-${uid}-${i}`}
          />
        ))}
      </svg>

      {/* C-004 fix: endLabel de TODAS as séries (não só a primeira) com cor própria */}
      {series.some((s) => s.endLabel) && (
        <div style={{ display: "flex", justifyContent: "space-around", marginTop: 20 }}>
          {series.filter((s) => s.endLabel).map((s, i) => (
            <span
              key={i}
              style={{
                fontWeight: 700,
                fontSize: labelFontSize,
                color: s.color,
                textTransform: "uppercase" as const,
                textAlign: "center" as const,
              }}
            >
              {s.endLabel}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Usage Example ─────────────────────────────────────────────────────────────
/*
// Comparação boca-a-boca vs tráfego pago
<SVGGrowthLine
  startFrame={BEAT.dado}
  series={[
    {
      color: "#00DF82",
      curveType: "slow-start",
      endLabel: "Cresce sozinho",
    },
  ]}
  width={240}
  height={154}
/>

// Comparação lado-a-lado: dois gráficos individuais dentro de dois cards
// (renderizar cada card com um SVGGrowthLine próprio)
*/

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
